import { useState, useEffect, useCallback } from 'react'
import { authService } from '../services/auth'
import { UserProfile, UserRegistrationForm, AuthState } from '../types/user'

export const useAuth = () => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    loading: true,
    error: null
  })

  // Initialize auth state listener
  useEffect(() => {
    const unsubscribe = authService.onAuthStateChanged((user) => {
      setAuthState({
        user,
        loading: false,
        error: null
      })
    })

    return unsubscribe
  }, [])

  // Register new user
  const register = useCallback(async (userData: UserRegistrationForm): Promise<void> => {
    setAuthState(prev => ({ ...prev, loading: true, error: null }))
    
    try {
      await authService.registerUser(userData)
      // Auth state will be updated by the listener
    } catch (error: any) {
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: error.message
      }))
      throw error
    }
  }, [])

  // Sign in user
  const signIn = useCallback(async (email: string, password: string): Promise<void> => {
    setAuthState(prev => ({ ...prev, loading: true, error: null }))
    
    try {
      await authService.signIn(email, password)
      // Auth state will be updated by the listener
    } catch (error: any) {
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: error.message
      }))
      throw error
    }
  }, [])

  // Sign out user
  const signOut = useCallback(async (): Promise<void> => {
    setAuthState(prev => ({ ...prev, loading: true, error: null }))
    
    try {
      await authService.signOut()
      // Auth state will be updated by the listener
    } catch (error: any) {
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: error.message
      }))
      throw error
    }
  }, [])

  // Send password reset email
  const sendPasswordReset = useCallback(async (email: string): Promise<void> => {
    setAuthState(prev => ({ ...prev, error: null }))
    
    try {
      await authService.sendPasswordReset(email)
    } catch (error: any) {
      setAuthState(prev => ({
        ...prev,
        error: error.message
      }))
      throw error
    }
  }, [])

  // Update user profile
  const updateProfile = useCallback(async (updates: Partial<UserProfile>): Promise<void> => {
    if (!authState.user) {
      throw new Error('No user signed in')
    }

    setAuthState(prev => ({ ...prev, loading: true, error: null }))
    
    try {
      await authService.updateUserProfile(authState.user.uid, updates)
      
      // Update local state
      setAuthState(prev => ({
        ...prev,
        user: prev.user ? { ...prev.user, ...updates } : null,
        loading: false
      }))
    } catch (error: any) {
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: error.message
      }))
      throw error
    }
  }, [authState.user])

  // Clear error
  const clearError = useCallback(() => {
    setAuthState(prev => ({ ...prev, error: null }))
  }, [])

  // Refresh user profile
  const refreshUserProfile = useCallback(async (): Promise<void> => {
    if (authState.user) {
      try {
        const userProfile = await authService.getUserProfile(authState.user.uid)
        if (userProfile) {
          setAuthState(prev => ({
            ...prev,
            user: userProfile
          }))
        }
      } catch (error) {
        console.error('Error refreshing user profile:', error)
      }
    }
  }, [authState.user])

  return {
    user: authState.user,
    loading: authState.loading,
    error: authState.error,
    register,
    signIn,
    signOut,
    sendPasswordReset,
    updateProfile,
    clearError,
    isAuthenticated: !!authState.user,
    isEmailVerified: authState.user?.emailVerified || false,
    refreshUserProfile
  }
} 